import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Mail, Lock, LogIn, KeyRound, Send, User, UserPlus, MailCheck, Loader2, Eye, EyeOff } from 'lucide-react';
import api, { login, guardarSesion } from '../api';
import ecuaflyLogo from '../assets/ecuafly-logo.png';

// Además de login/forgot/reset, hay modo "registro": autorregistro público
// de clientes nuevos (antes las cuentas solo llegaban migradas de Sheets o
// invitadas por un admin — ver importar_clientes.py en el backend, que
// sigue siendo el camino para altas masivas). "pendiente" y "activando" son
// parte del mismo flujo: la cuenta nace inactiva hasta que confirma el
// correo (evita cuentas creadas con un correo inválido/ajeno, que quedaban
// "creadas" pero sin forma de recibir nada).
function Login({ onLogin, resetToken, activarToken, uid }) {
  const [modo, setModo] = useState('login'); // 'login' | 'registro' | 'pendiente' | 'activando' | 'forgot' | 'reset'
  const [datos, setDatos] = useState({
    username: '', password: '', newPassword: '', firstName: '', lastName: '',
    aceptaTerminos: false, autorizaContacto: false,
  });
  const [cargando, setCargando] = useState(false);
  const [correoRegistrado, setCorreoRegistrado] = useState('');
  const [verPassword, setVerPassword] = useState(false);
  const [verNewPassword, setVerNewPassword] = useState(false);

  useEffect(() => {
    if (resetToken && uid) setModo('reset');
  }, [resetToken, uid]);

  useEffect(() => {
    if (!activarToken || !uid) return;
    setModo('activando');
    api.post('/auth/activar/', { uid, token: activarToken })
      .then((res) => {
        guardarSesion(res.data.token, res.data.email, res.data.rol, res.data.onboarding_completado, res.data.codigo_casillero);
        window.history.replaceState({}, document.title, '/');
        toast.success('¡Cuenta activada! Ya puedes usar el panel.');
        onLogin(res.data.rol);
      })
      .catch((err) => {
        window.history.replaceState({}, document.title, '/');
        toast.error(err.response?.data?.error || 'El enlace de activación es inválido o expiró.');
        setModo('login');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activarToken, uid]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (modo === 'forgot') {
      if (!datos.username.includes('@')) return toast.error('Ingresa un correo válido.');
      setCargando(true);
      try {
        const res = await api.post('/auth/password-reset/', { email: datos.username.trim() });
        toast.success(res.data.message || 'Instrucciones enviadas.');
        setModo('login');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Error al enviar el correo.');
      } finally {
        setCargando(false);
      }
      return;
    }

    if (modo === 'reset') {
      if (datos.newPassword.length < 6) return toast.error('La contraseña debe tener al menos 6 caracteres.');
      setCargando(true);
      try {
        const res = await api.post('/auth/password-reset/confirm/', {
          uid, token: resetToken, new_password: datos.newPassword,
        });
        toast.success(res.data.message || 'Contraseña actualizada.');
        window.history.replaceState({}, document.title, '/');
        setModo('login');
      } catch (err) {
        toast.error(err.response?.data?.error || 'El enlace es inválido o expiró.');
      } finally {
        setCargando(false);
      }
      return;
    }

    if (modo === 'registro') {
      if (!datos.username.includes('@')) return toast.error('Ingresa un correo válido.');
      if (!datos.firstName.trim()) return toast.error('Ingresa tu nombre.');
      if (datos.password.length < 8) return toast.error('La contraseña debe tener al menos 8 caracteres.');
      if (!datos.aceptaTerminos) return toast.error('Debes aceptar los Términos y Condiciones.');
      if (!datos.autorizaContacto) return toast.error('Debes autorizar el contacto por WhatsApp o correo.');
      setCargando(true);
      try {
        const res = await api.post('/auth/register/', {
          email: datos.username.trim(),
          password: datos.password,
          first_name: datos.firstName.trim(),
          last_name: datos.lastName.trim(),
          acepta_terminos: datos.aceptaTerminos,
          autoriza_contacto: datos.autorizaContacto,
        });
        setCorreoRegistrado(res.data.email);
        setModo('pendiente');
      } catch (err) {
        toast.error(err.response?.data?.error || 'No se pudo crear la cuenta.');
      } finally {
        setCargando(false);
      }
      return;
    }

    setCargando(true);
    try {
      const res = await login(datos.username.trim(), datos.password);
      onLogin(res.rol);
    } catch (err) {
      toast.error(
        err.response?.status === 400
          ? 'Credenciales incorrectas.'
          : (err.response?.data?.error || 'Error de conexión.')
      );
    } finally {
      setCargando(false);
    }
  };

  const titulo = modo === 'registro' ? 'Crea tu cuenta'
    : modo === 'pendiente' ? 'Confirma tu correo'
    : modo === 'activando' ? 'Activando tu cuenta'
    : modo === 'forgot' ? 'Recuperar contraseña'
    : modo === 'reset' ? 'Nueva contraseña'
    : 'Bienvenido de vuelta';

  return (
    <div className="login-wrapper">
      <div className="login-card">
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src={ecuaflyLogo} alt="Ecuafly" style={{ height: '96px', margin: '0 auto 16px', display: 'block' }} />
          <h2 style={{ marginBottom: '4px' }}>{titulo}</h2>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            {modo === 'registro' ? <UserPlus size={14} />
              : modo === 'pendiente' ? <MailCheck size={14} />
              : modo === 'activando' ? <Loader2 size={14} className="lucide-spin" />
              : modo === 'forgot' || modo === 'reset' ? <KeyRound size={14} />
              : null}
            Acceso de clientes
          </p>
        </div>

        {modo === 'pendiente' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--color-text-primary)' }}>
              Te enviamos un correo a <strong>{correoRegistrado}</strong> con un enlace para activar tu cuenta.
            </p>
            <p className="text-secondary text-sm">
              Revisa también la carpeta de spam. Sin activarla, tu registro queda pendiente y no vas a poder entrar.
            </p>
            <button
              type="button"
              className="btn-secondary btn-block"
              style={{ marginTop: 'var(--spacing-4)' }}
              onClick={() => { setModo('login'); setDatos({ username: '', password: '', newPassword: '', firstName: '', lastName: '', aceptaTerminos: false, autorizaContacto: false }); }}
            >
              Volver al inicio de sesión
            </button>
          </div>
        )}

        {modo === 'activando' && (
          <p className="text-secondary text-sm" style={{ textAlign: 'center' }}>
            Un momento, estamos confirmando tu correo...
          </p>
        )}

        {modo !== 'pendiente' && modo !== 'activando' && (
        <>
        <form onSubmit={handleSubmit} className="login-form">
          {modo === 'registro' && (
            <div className="form-row" style={{ marginBottom: 0 }}>
              <div style={{ position: 'relative' }}>
                <User size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
                <input
                  type="text" placeholder="Nombre" autoFocus required
                  value={datos.firstName}
                  onChange={(e) => setDatos({ ...datos, firstName: e.target.value })}
                  style={{ paddingLeft: '40px' }}
                />
              </div>
              <div style={{ position: 'relative' }}>
                <User size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
                <input
                  type="text" placeholder="Apellido"
                  value={datos.lastName}
                  onChange={(e) => setDatos({ ...datos, lastName: e.target.value })}
                  style={{ paddingLeft: '40px' }}
                />
              </div>
            </div>
          )}

          {(modo === 'login' || modo === 'registro' || modo === 'forgot') && (
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
              <input
                // type="text" en vez de "email": el backend autentica por
                // username (normalmente el correo, pero un admin puede tener
                // un username sin forma de email), así que no conviene que
                // la validación nativa del navegador bloquee el envío.
                type="text" placeholder="tucorreo@ejemplo.com" autoFocus={modo !== 'registro'} required
                value={datos.username}
                onChange={(e) => setDatos({ ...datos, username: e.target.value })}
                style={{ paddingLeft: '40px' }}
              />
            </div>
          )}

          {(modo === 'login' || modo === 'registro') && (
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
              <input
                type={verPassword ? 'text' : 'password'} placeholder="Contraseña" required
                value={datos.password}
                onChange={(e) => setDatos({ ...datos, password: e.target.value })}
                style={{ paddingLeft: '40px', paddingRight: '40px' }}
              />
              <button type="button" onClick={() => setVerPassword(!verPassword)} tabIndex={-1}
                style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: '4px' }}>
                {verPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          )}

          {modo === 'reset' && (
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
              <input
                type={verNewPassword ? 'text' : 'password'} placeholder="Nueva contraseña" required
                value={datos.newPassword}
                onChange={(e) => setDatos({ ...datos, newPassword: e.target.value })}
                style={{ paddingLeft: '40px', paddingRight: '40px' }}
              />
              <button type="button" onClick={() => setVerNewPassword(!verNewPassword)} tabIndex={-1}
                style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: '4px' }}>
                {verNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          )}

          {modo === 'registro' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px', fontSize: '13px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                <input
                  type="checkbox"
                  checked={datos.aceptaTerminos}
                  onChange={(e) => setDatos({ ...datos, aceptaTerminos: e.target.checked })}
                  style={{ marginTop: '2px', flexShrink: 0 }}
                />
                <span>Acepto los <a href="https://ecuafly.com/terminos-y-condiciones/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent-hover)' }}>Términos y Condiciones</a> y la <a href="https://ecuafly.com/politica-privacidad/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent-hover)' }}>Política de Privacidad</a> de Ecuafly.</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                <input
                  type="checkbox"
                  checked={datos.autorizaContacto}
                  onChange={(e) => setDatos({ ...datos, autorizaContacto: e.target.checked })}
                  style={{ marginTop: '2px', flexShrink: 0 }}
                />
                <span>Autorizo a Ecuafly a contactarme por WhatsApp o correo electrónico para notificaciones relacionadas con mi cuenta, paquetes y servicios solicitados.</span>
              </label>
            </div>
          )}

          <button type="submit" disabled={cargando} className="btn-primary btn-block" style={{ padding: '12px', marginTop: '8px' }}>
            {modo === 'login' && <LogIn size={18} />}
            {modo === 'registro' && <UserPlus size={18} />}
            {(modo === 'forgot' || modo === 'reset') && <Send size={18} />}
            {cargando ? 'Cargando...' : (
              modo === 'login' ? 'Iniciar sesión'
                : modo === 'registro' ? 'Crear cuenta'
                : modo === 'forgot' ? 'Enviar enlace'
                : 'Actualizar contraseña'
            )}
          </button>
        </form>

        <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {(modo === 'login' || modo === 'registro') && (
            <div>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {modo === 'registro' ? '¿Ya tienes cuenta?' : '¿No tienes cuenta?'}
              </span>{' '}
              <button
                type="button"
                onClick={() => {
                  setModo(modo === 'registro' ? 'login' : 'registro');
                  setDatos({ username: '', password: '', newPassword: '', firstName: '', lastName: '', aceptaTerminos: false, autorizaContacto: false });
                }}
                style={{ background: 'none', border: 'none', color: 'var(--color-accent-hover)', fontWeight: 600, cursor: 'pointer' }}
              >
                {modo === 'registro' ? 'Inicia sesión' : 'Regístrate'}
              </button>
            </div>
          )}
          {modo === 'login' && (
            <button type="button" onClick={() => { setModo('forgot'); setDatos({ username: '', password: '', newPassword: '' }); }}
              style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', textDecoration: 'underline' }}>
              ¿Olvidaste tu contraseña?
            </button>
          )}
          {(modo === 'forgot' || modo === 'reset') && (
            <button type="button" onClick={() => {
              setModo('login');
              setDatos({ username: '', password: '', newPassword: '' });
              if (modo === 'reset') window.history.replaceState({}, document.title, '/');
            }} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', textDecoration: 'underline' }}>
              Volver al inicio de sesión
            </button>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default Login;
